import { useState } from 'react';
import { AlertCircle, CheckCircle2, Clock3, RefreshCw, Send, Wrench } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { Link } from 'react-router-dom';
import ROUTE from 'src/utils/routeConstants';
import { tyreManagementApi, unwrapList } from '../api/tyreManagementApi';
import useAsyncData from '../hooks/useAsyncData';
import { tyreQueryKeys } from '../api/queryKeys';
import useTyreMutation from '../hooks/useTyreMutation';
import ModuleHeader from '../components/ModuleHeader';
import EmptyState from '../components/EmptyState';
import StateBadge from '../components/StateBadge';
import FormField from '../components/FormField';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { formatDate } from '../lib/utils';
import { StatCardsSkeleton, TableSkeleton } from '../components/TmSkeletonLayouts';

const currentLocal = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

const loadAllPages = async (getPage) => {
  const loadPage = async (page, accumulated) => {
    const response = await getPage(page);
    const items = [...accumulated, ...unwrapList(response)];
    return response?.next ? loadPage(page + 1, items) : items;
  };
  return loadPage(1, []);
};

const emptyRetreadForm = () => ({
  effectiveAt: currentLocal(),
  vendor: '',
  remarks: '',
  treadDepth: '',
  cost: '',
  invoice: '',
  idempotencyKey: uuidv4(),
});

const TyreRetreadingPage = () => {
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(emptyRetreadForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const retreadingMutation = useTyreMutation(({ type, payload }) => (type === 'send'
    ? tyreManagementApi.sendForRetreading(payload)
    : tyreManagementApi.completeRetreading(payload)));
  const dueState = useAsyncData(tyreQueryKeys.tyres({ retreading_alerts: true }), async () => {
    const [due, overdue] = await Promise.all([
      loadAllPages((page) => tyreManagementApi.getTyres({ alert_state: 'due', custody: 'available', page, page_size: 100 })),
      loadAllPages((page) => tyreManagementApi.getTyres({ alert_state: 'overdue', custody: 'available', page, page_size: 100 })),
    ]);
    return [...overdue, ...due];
  });
  const cyclesState = useAsyncData(tyreQueryKeys.retreading({ page_size: 100 }), () => loadAllPages(
    (page) => tyreManagementApi.getRetreading({ page, page_size: 100 })
  ));
  const cycles = unwrapList(cyclesState.data);
  const sent = cycles.filter((cycle) => cycle.status === 'sent');
  const completed = cycles.filter((cycle) => cycle.status === 'completed');

  const openDialog = (type, item) => {
    setError('');
    setSuccess('');
    setForm(emptyRetreadForm());
    setDialog({ type, item });
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (dialog.type === 'send') {
        await retreadingMutation.mutateAsync({ type: 'send', payload: { tyre_id: dialog.item.id, effective_at: new Date(form.effectiveAt).toISOString(), vendor: form.vendor, remarks: form.remarks, idempotency_key: form.idempotencyKey } });
      } else {
        await retreadingMutation.mutateAsync({ type: 'complete', payload: { retread_cycle_id: dialog.item.id, effective_at: new Date(form.effectiveAt).toISOString(), tread_depth_mm: form.treadDepth, cost: form.cost, invoice_number: form.invoice, remarks: form.remarks, idempotency_key: form.idempotencyKey } });
      }
      setSuccess(dialog.type === 'send' ? 'Tyre sent for re-treading.' : 'Re-treading return recorded.');
      setDialog(null);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <ModuleHeader eyebrow="Lifecycle renewal" title="Re-treading" description="Send due tyres to a vendor and record their return." actions={<><Button asChild variant="outline"><Link to={ROUTE.tyreV2Vehicles}>Remove fitted tyres</Link></Button><Button variant="outline" onClick={() => Promise.all([dueState.reload(), cyclesState.reload()])}><RefreshCw className="h-4 w-4" /> Refresh</Button></>} />
      {success && <div className="flex gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800" role="status"><CheckCircle2 className="h-5 w-5 shrink-0" />{success}</div>}
      {(dueState.loading || cyclesState.loading) ? (
        <StatCardsSkeleton count={3} columns="sm:grid-cols-3" />
      ) : (
        <section className="grid gap-4 sm:grid-cols-3"><Card><CardContent className="flex items-center justify-between p-5"><div><p className="text-xs font-bold uppercase tracking-wider text-slate-500">Awaiting dispatch</p><p className="mt-2 text-3xl font-extrabold text-orange-700">{dueState.data?.length || 0}</p></div><Wrench className="h-7 w-7 text-orange-500" /></CardContent></Card><Card><CardContent className="flex items-center justify-between p-5"><div><p className="text-xs font-bold uppercase tracking-wider text-slate-500">With vendor</p><p className="mt-2 text-3xl font-extrabold text-blue-700">{sent.length}</p></div><Clock3 className="h-7 w-7 text-blue-500" /></CardContent></Card><Card><CardContent className="flex items-center justify-between p-5"><div><p className="text-xs font-bold uppercase tracking-wider text-slate-500">Completed</p><p className="mt-2 text-3xl font-extrabold text-emerald-700">{completed.length}</p></div><CheckCircle2 className="h-7 w-7 text-emerald-500" /></CardContent></Card></section>
      )}
      <Card><CardHeader><CardTitle>Ready to send</CardTitle><p className="text-sm text-slate-500">Only tyres removed from vehicles can be sent. Use “Remove fitted tyres” above when a due tyre is still on a vehicle.</p></CardHeader><CardContent className="px-0 pb-0">{dueState.loading ? <TableSkeleton rows={4} columns={5} /> : dueState.error ? <div className="px-6 pb-6"><EmptyState title="Ready-to-send tyres unavailable" description={dueState.error} action={<Button onClick={dueState.reload}>Try again</Button>} /></div> : !dueState.data?.length ? <div className="px-6 pb-6"><EmptyState title="No tyres ready to send" description="No due tyres are ready to send." /></div> : <Table><TableHeader><TableRow><TableHead>Tyre</TableHead><TableHead>Site</TableHead><TableHead>Condition</TableHead><TableHead>Priority</TableHead><TableHead /></TableRow></TableHeader><TableBody>{(dueState.data || []).map((tyre) => <TableRow key={tyre.id}><TableCell><p className="font-extrabold text-slate-900">{tyre.tyre_id}</p><p className="text-xs text-slate-500">{tyre.brand} {tyre.model} · {tyre.size}</p></TableCell><TableCell>{tyre.site_name}</TableCell><TableCell><StateBadge value={tyre.condition} /></TableCell><TableCell><StateBadge value={tyre.alert_state} /></TableCell><TableCell><Button size="sm" onClick={() => openDialog('send', tyre)}><Send className="h-4 w-4" /> Send</Button></TableCell></TableRow>)}</TableBody></Table>}</CardContent></Card>
      <Card><CardHeader><CardTitle>With re-treading vendor</CardTitle><p className="text-sm text-slate-500">Record return details after the tyre is received and inspected.</p></CardHeader><CardContent className="px-0 pb-0">{cyclesState.loading ? <TableSkeleton rows={4} columns={5} /> : cyclesState.error ? <div className="px-6 pb-6"><EmptyState title="Re-treading history unavailable" description={cyclesState.error} action={<Button onClick={cyclesState.reload}>Try again</Button>} /></div> : !sent.length ? <div className="px-6 pb-6"><EmptyState title="No tyres with vendors" description="Sent tyres will appear here until they are returned." /></div> : <Table><TableHeader><TableRow><TableHead>Tyre</TableHead><TableHead>Vendor</TableHead><TableHead>Sent on</TableHead><TableHead>Cycle</TableHead><TableHead /></TableRow></TableHeader><TableBody>{sent.map((cycle) => <TableRow key={cycle.id}><TableCell className="font-extrabold text-slate-900">{cycle.tyre_id}</TableCell><TableCell>{cycle.vendor}</TableCell><TableCell>{formatDate(cycle.sent_at)}</TableCell><TableCell>Re-tread #{cycle.number}</TableCell><TableCell><Button size="sm" onClick={() => openDialog('complete', cycle)}><CheckCircle2 className="h-4 w-4" /> Complete return</Button></TableCell></TableRow>)}</TableBody></Table>}</CardContent></Card>
      <Dialog open={Boolean(dialog)} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialog?.type === 'send' ? 'Send for re-treading' : 'Complete re-treading'}</DialogTitle>
            <DialogDescription>
              {dialog?.item?.tyre_id}. The change will be recorded in history.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="flex min-h-0 min-w-0 flex-1 flex-col">
            <DialogBody className="min-w-0 space-y-5">
              <FormField
                label={dialog?.type === 'send' ? 'Sent date and time' : 'Received date and time'}
                htmlFor="retread-effective"
                required
              >
                <Input
                  id="retread-effective"
                  type="datetime-local"
                  value={form.effectiveAt}
                  onChange={(event) => setForm({ ...form, effectiveAt: event.target.value })}
                  required
                />
              </FormField>

              {dialog?.type === 'send' ? (
                <FormField label="Re-treading vendor" htmlFor="retread-vendor" required>
                  <Input
                    id="retread-vendor"
                    value={form.vendor}
                    onChange={(event) => setForm({ ...form, vendor: event.target.value })}
                    required
                  />
                </FormField>
              ) : (
                <div className="grid min-w-0 gap-4 sm:grid-cols-2">
                  <FormField label="New tread depth (mm)" htmlFor="retread-depth" required>
                    <Input
                      id="retread-depth"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.treadDepth}
                      onChange={(event) => setForm({ ...form, treadDepth: event.target.value })}
                      required
                    />
                  </FormField>
                  <FormField label="Re-treading cost" htmlFor="retread-cost" required>
                    <Input
                      id="retread-cost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.cost}
                      onChange={(event) => setForm({ ...form, cost: event.target.value })}
                      required
                    />
                  </FormField>
                  <FormField label="Invoice number" htmlFor="retread-invoice">
                    <Input
                      id="retread-invoice"
                      value={form.invoice}
                      onChange={(event) => setForm({ ...form, invoice: event.target.value })}
                    />
                  </FormField>
                </div>
              )}

              <FormField label="Remarks" htmlFor="retread-remarks">
                <Textarea
                  id="retread-remarks"
                  value={form.remarks}
                  onChange={(event) => setForm({ ...form, remarks: event.target.value })}
                />
              </FormField>

              {error && (
                <div className="flex gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <RefreshCw className="h-4 w-4 animate-spin" />}
                {submitting ? 'Saving…' : dialog?.type === 'send' ? 'Confirm dispatch' : 'Complete return'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TyreRetreadingPage;
